/**
 * @file app/tauri/escposRenderer.js
 *
 * Self-contained ESC/POS receipt renderer for the Tauri (desktop) build.
 *
 * Tauri has no silent HTML print, so the desktop build standardizes on direct
 * ESC/POS bytes — the robust thermal-POS approach — sent over TCP
 * (`print_escpos_tcp`), the Windows RAW spooler / CUPS raw (`print_raw_to_printer`),
 * or a serial / Bluetooth-Classic SPP port.
 *
 * This module has NO native dependency (no node-thermal-printer / canvas): a
 * tiny encoder builds the byte stream, with a full CP852 (Latin-2) table so
 * Hungarian accented characters render correctly. The layout mirrors the
 * Android receipt and the Happy-POS builder (header, items, totals, payments,
 * VAT/ÁFA table, footer) so receipts look identical on every platform.
 *
 * Ported from Happy-POS `src/tauri/escposRenderer.js`.
 */

/* eslint-disable no-bitwise */

import { getInvoiceDetails, orderNeedsInvoice } from "../helpers/invoiceDetails";
import { computeReceiptTotals, getVatSummary, resolveChargedUnitPrice } from "../helpers/vatSummary";

// CP852 (IBM852 / Latin-2) upper half, index 0 = byte 0x80 … 0xFF.
const CP852 = [
  0x00c7, 0x00fc, 0x00e9, 0x00e2, 0x00e4, 0x016f, 0x0107, 0x00e7,
  0x0142, 0x00eb, 0x0150, 0x0151, 0x00ee, 0x0179, 0x00c4, 0x0106,
  0x00c9, 0x0139, 0x013a, 0x00f4, 0x00f6, 0x013d, 0x013e, 0x015a,
  0x015b, 0x00d6, 0x00dc, 0x0164, 0x0165, 0x0141, 0x00d7, 0x010d,
  0x00e1, 0x00ed, 0x00f3, 0x00fa, 0x0104, 0x0105, 0x017d, 0x017e,
  0x0118, 0x0119, 0x00ac, 0x017a, 0x010c, 0x015f, 0x00ab, 0x00bb,
  0x2591, 0x2592, 0x2593, 0x2502, 0x2524, 0x00c1, 0x00c2, 0x011a,
  0x015e, 0x2563, 0x2551, 0x2557, 0x255d, 0x017b, 0x017c, 0x2510,
  0x2514, 0x2534, 0x252c, 0x251c, 0x2500, 0x253c, 0x0102, 0x0103,
  0x255a, 0x2554, 0x2569, 0x2566, 0x2560, 0x2550, 0x256c, 0x00a4,
  0x0111, 0x0110, 0x010e, 0x00cb, 0x010f, 0x0147, 0x00cd, 0x00ce,
  0x011b, 0x2518, 0x250c, 0x2588, 0x2584, 0x0162, 0x016e, 0x2580,
  0x00d3, 0x00df, 0x00d4, 0x0143, 0x0144, 0x0148, 0x0160, 0x0161,
  0x0154, 0x00da, 0x0155, 0x0170, 0x00fd, 0x00dd, 0x0157, 0x00b4,
  0x00ad, 0x02dd, 0x02db, 0x02c7, 0x02d8, 0x00a7, 0x00f7, 0x00b8,
  0x00b0, 0x00a8, 0x02d9, 0x0171, 0x0158, 0x0159, 0x25a0, 0x00a0,
];

const UNICODE_TO_CP852 = (() => {
  const map = new Map();
  for (let i = 0; i < CP852.length; i += 1) map.set(CP852[i], 0x80 + i);
  return map;
})();

// Best-effort ASCII fallback for characters outside CP852.
const ASCII_FALLBACK = {
  0x2013: 0x2d, 0x2014: 0x2d, 0x2018: 0x27, 0x2019: 0x27, 0x201c: 0x22,
  0x201d: 0x22, 0x2026: 0x2e, 0x20ac: 0x45,
};

/** Encode a string into CP852 bytes. */
function encodeCp852(str) {
  const out = [];
  for (const ch of String(str)) {
    const cp = ch.codePointAt(0);
    if (cp < 0x80) out.push(cp);
    else if (UNICODE_TO_CP852.has(cp)) out.push(UNICODE_TO_CP852.get(cp));
    else if (ASCII_FALLBACK[cp]) out.push(ASCII_FALLBACK[cp]);
    else out.push(0x3f); // '?'
  }
  return out;
}

const CHARS_80MM = 48;
const CHARS_60MM = 32;

/** Approx. characters-per-line for a given paper width (mm). 60mm→32, 80mm→48. */
export const resolveCharWidth = (paperWidthMm) => {
  const w = Number(paperWidthMm);
  if (!Number.isFinite(w) || w <= 0) return CHARS_80MM;
  if (w <= 60) return CHARS_60MM;
  if (w >= 80 && w < 82) return CHARS_80MM;
  return Math.max(24, Math.min(96, Math.round(w * 0.6)));
};

export const formatPriceHu = (value = 0) => {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat("hu-HU", {
      style: "currency",
      currency: "HUF",
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  } catch (_e) {
    return `${Math.round(amount).toLocaleString("hu-HU")} Ft`;
  }
};

/** Minimal dependency-free ESC/POS byte builder. */
export class EscPos {
  constructor(charsPerLine) {
    this.bytes = [];
    this.cols = charsPerLine;
    this.push([0x1b, 0x40]); // ESC @ — initialize
    this.push([0x1b, 0x74, 18]); // ESC t 18 — select CP852
    this.bold(true); // dark output for thermal legibility
  }

  push(arr) {
    for (let i = 0; i < arr.length; i += 1) this.bytes.push(arr[i] & 0xff);
    return this;
  }

  raw(str) {
    return this.push(encodeCp852(str));
  }

  align(mode) {
    const n = mode === "center" ? 1 : mode === "right" ? 2 : 0;
    return this.push([0x1b, 0x61, n]);
  }

  bold(on) {
    return this.push([0x1b, 0x45, on ? 1 : 0]);
  }

  size(mult) {
    const m = (mult || 1) - 1;
    return this.push([0x1d, 0x21, (m << 4) | m]);
  }

  newline(n = 1) {
    for (let i = 0; i < n; i += 1) this.push([0x0a]);
    return this;
  }

  line(str = "") {
    return this.raw(str).newline();
  }

  rule(ch = "-") {
    return this.line(ch.repeat(this.cols));
  }

  /** Left text + right text padded to a full line. */
  leftRight(left, right) {
    const l = String(left);
    const r = String(right);
    const space = Math.max(1, this.cols - l.length - r.length);
    if (l.length + r.length >= this.cols) {
      return this.line((l + " " + r).slice(0, this.cols));
    }
    return this.line(l + " ".repeat(space) + r);
  }

  /** Columns: [{text, align, width(fraction 0..1)}]. */
  table(columns) {
    let lineStr = "";
    columns.forEach((col) => {
      const w = Math.max(1, Math.round((col.width || 0) * this.cols));
      let text = String(col.text == null ? "" : col.text);
      if (text.length > w) text = text.slice(0, w);
      const pad = w - text.length;
      lineStr += col.align === "RIGHT" ? " ".repeat(pad) + text : text + " ".repeat(pad);
    });
    return this.line(lineStr.slice(0, this.cols));
  }

  cut() {
    return this.newline(4).push([0x1d, 0x56, 0x42, 0x00]); // feed + partial cut
  }

  encode() {
    return Uint8Array.from(this.bytes);
  }
}

// ---- order accessors (mirror the Android receipt builder) ------------------
const safe = (v) => (v === null || v === undefined ? "" : String(v));
const getItemName = (item = {}) =>
  item.remoteName || item.name || item?.item?.name?.hu || item?.item?.name?.en || "Termék";
const getItemQty = (item = {}) => Number(item.amount || item.quantity || 1);
const getItemUnitPrice = (item = {}) =>
  Number(item.remotePrice || item.price || item?.item?.sizes?.[0]?.price || 0);
const getItemSize = (item = {}) => {
  const sizes = item?.item?.sizes || [];
  const bySizeId = sizes.find((s) => s && item.size && s._id === item.size);
  return bySizeId || sizes[item.sizeIndex] || sizes[0] || null;
};
const getOrderTypeLabel = (order = {}) => {
  const t = order.orderType || "takeAway";
  if (t === "dineIn") return "Helyben fogyasztás";
  if (t === "takeAway") return "Elvitelre";
  if (t === "online") return "Házhozszállítás";
  if (t === "preOrder") return "Előrendelés";
  return t;
};
const mapPaymentName = (type = "") => {
  if (type === "cash") return "Készpénz";
  if (type === "creditCard") return "Bankkártya";
  if (type === "szepCreditCard") return "SZÉP kártya";
  if (type === "voucher") return "Utalvány";
  if (type === "transfer") return "Átutalás";
  return type || "Fizetés";
};

/**
 * Render a full receipt to ESC/POS bytes.
 * @param {object} order order payload (same shape as the print job)
 * @param {{paperWidthMm?:number}} printerConfig
 * @returns {Uint8Array}
 */
export function renderReceipt(order = {}, printerConfig = {}) {
  const paperWidthMm = Number(printerConfig.paperWidthMm) <= 60 ? 60 : 80;
  const cols = resolveCharWidth(paperWidthMm);
  const p = new EscPos(cols);

  const items = [...(order.items || [])];
  const payments = [...(order.paymentTypes || [])];
  const restaurant = order.restaurant || {};
  const place = order.restaurantPlace || {};

  // Header
  p.align("center");
  if (restaurant.name) p.size(2).line(safe(restaurant.name)).size(1);
  const placeLine = place.name || place.raw || place.address || "";
  if (placeLine) p.line(safe(placeLine));
  if (restaurant.taxId || order.restaurantTaxId) {
    p.line("Adószám: " + safe(restaurant.taxId || order.restaurantTaxId));
  }
  p.newline();
  p.size(2).line("#" + safe(order.orderCode || order.generatedId || "")).size(1);
  if (order.orderTime) {
    try {
      p.line(new Date(order.orderTime).toLocaleString("hu-HU"));
    } catch (_e) {
      /* noop */
    }
  }
  p.line(getOrderTypeLabel(order));
  if (order.questName) p.line(safe(order.questName));
  if (order.orderType === "online" && order.addressInfo) {
    const info = order.addressInfo;
    if (info.zip && info.city) p.line(`${safe(info.zip)} ${safe(info.city)}`);
    if (info.address) p.line(safe(info.address));
  }
  p.rule();

  // Items
  p.align("left");
  items.forEach((item) => {
    const qty = getItemQty(item);
    // Unsent cart lines keep `remotePrice` as the LINE total; resolve the charged
    // unit price like the Fiscat rows (list/size price only as a last resort).
    const chargedUnitPrice = resolveChargedUnitPrice(item, qty);
    const unitPrice = chargedUnitPrice > 0 ? chargedUnitPrice : getItemUnitPrice(item);
    const lineTotal = Math.round(unitPrice * qty);
    const size = getItemSize(item);
    const sizeSuffix = size && Number(size.amount) !== 1 ? ` (${size.amount}${size.unit || ""})` : "";
    const name = getItemName(item) + sizeSuffix;

    p.table([
      { text: `${qty}x`, align: "LEFT", width: 0.12 },
      { text: name, align: "LEFT", width: 0.58 },
      { text: formatPriceHu(lineTotal), align: "RIGHT", width: 0.3 },
    ]);
    if (qty > 1) {
      p.table([
        { text: "", align: "LEFT", width: 0.12 },
        { text: `  ${formatPriceHu(unitPrice)} / db`, align: "LEFT", width: 0.88 },
      ]);
    }
    [...(item.extras || [])].forEach((extra) => {
      const extraName = extra.name || extra?.item?.name?.hu || extra?.item?.name?.en || "Extra";
      const extraPrice = Number(extra.remotePrice || extra.price || 0);
      p.table([
        { text: "", align: "LEFT", width: 0.12 },
        { text: `+ ${extraName}`, align: "LEFT", width: 0.58 },
        { text: extraPrice > 0 ? formatPriceHu(extraPrice * qty) : "", align: "RIGHT", width: 0.3 },
      ]);
    });
    if (item.comment) {
      p.table([
        { text: "", align: "LEFT", width: 0.12 },
        { text: `"${safe(item.comment)}"`, align: "LEFT", width: 0.88 },
      ]);
    }
  });
  p.rule();

  // Totals — from the same charged rows as the Fiscat register
  // (computeReceiptTotals), so products + service fee + delivery − discount
  // always equals the total. A local dine-in order's `orderSum` can lack the
  // szervízdíj, so it is only the fallback for orders without priced items.
  const receiptTotals = computeReceiptTotals(order);
  const productsSum = receiptTotals
    ? receiptTotals.productsAmount
    : Number(
      order.productsSum ||
        items.reduce((sum, it) => {
          const extrasTotal = [...(it.extras || [])].reduce(
            (s, e) => s + Number(e.remotePrice || e.price || 0),
            0
          );
          return sum + (getItemUnitPrice(it) + extrasTotal) * getItemQty(it);
        }, 0)
    );
  const discountAmount = receiptTotals ? receiptTotals.discountAmount : Number(order?.discounts?.amount || 0);
  const discountPercentage = receiptTotals ? receiptTotals.discountPercentage : Number(order?.discounts?.percentage || 0);
  const serviceFee = receiptTotals ? receiptTotals.serviceFeeAmount : Number(order.serviceFeePrice || 0);
  const deliveryFee = receiptTotals ? receiptTotals.deliveryFeeAmount : Number(order.deliveryFee || 0);
  const tipAmount = Number(order.tipAmount || 0);
  const finalTotal = receiptTotals
    ? receiptTotals.total + tipAmount
    : Number(
      order.orderSum != null
        ? order.orderSum
        : productsSum - discountAmount + serviceFee + deliveryFee + tipAmount
    );

  p.leftRight("Termékek összesen", formatPriceHu(productsSum));
  if (discountAmount > 0) {
    const label = discountPercentage > 0 ? `Kedvezmény (${discountPercentage}%)` : "Kedvezmény";
    p.leftRight(label, `- ${formatPriceHu(discountAmount)}`);
  }
  if (serviceFee > 0) p.leftRight("Szervizdíj", formatPriceHu(serviceFee));
  if (deliveryFee > 0) p.leftRight("Szállítási díj", formatPriceHu(deliveryFee));
  if (tipAmount > 0) p.leftRight("Borravaló", formatPriceHu(tipAmount));

  p.rule();
  p.size(2).leftRight("FIZETENDŐ", formatPriceHu(finalTotal)).size(1);

  // Payments
  if (payments.length > 0) {
    p.newline();
    payments.forEach((payment) => p.leftRight(mapPaymentName(payment.paymentType), formatPriceHu(payment.amount)));
  }

  // VAT / ÁFA table — unified with the Android/RawBT receipt (oldPrinter.js).
  // FiscatQRService.getVatSummary prefers any pre-computed summary maps on the
  // order, else recomputes from items using the VAT-relevant `remotePrice`
  // (buildSaleData). Returns a normalized array of { vatRate, net, vat, gross }.
  let vatRows = [];
  try {
    vatRows = getVatSummary(order) || [];
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Receipt VAT breakdown skipped:", e?.message || e);
    vatRows = [];
  }
  if (Array.isArray(vatRows) && vatRows.length > 0) {
    p.rule();
    p.table([
      { text: "ÁFA", align: "LEFT", width: 0.25 },
      { text: "Nettó", align: "RIGHT", width: 0.25 },
      { text: "ÁFA", align: "RIGHT", width: 0.22 },
      { text: "Bruttó", align: "RIGHT", width: 0.28 },
    ]);
    vatRows.forEach((row) => {
      p.table([
        { text: `${row?.label != null ? String(row.label) : String(Number(row?.vatRate) || 0)}%`, align: "LEFT", width: 0.25 },
        { text: formatPriceHu(row?.net || 0), align: "RIGHT", width: 0.25 },
        { text: formatPriceHu(row?.vat || 0), align: "RIGHT", width: 0.22 },
        { text: formatPriceHu(row?.gross || 0), align: "RIGHT", width: 0.28 },
      ]);
    });
  }

  // VAT invoice request block — printed under the summary when the customer
  // asked for a (VAT) invoice.
  const invoiceDetails = orderNeedsInvoice(order) ? getInvoiceDetails(order) : null;
  if (invoiceDetails) {
    p.rule();
    p.align("center");
    p.bold(true).line("SZÁMLAIGÉNY / SZÁMLÁS RENDELÉS").bold(true);
    p.line("A vendég ÁFÁS számlát kért");
    p.align("left");
    if (invoiceDetails) {
      if (invoiceDetails.companyName) p.leftRight("Cég", safe(invoiceDetails.companyName));
      if (invoiceDetails.taxNumber) p.leftRight("Adószám", safe(invoiceDetails.taxNumber));
      if (invoiceDetails.address) p.leftRight("Cím", safe(invoiceDetails.address));
      if (invoiceDetails.name) p.leftRight("Név", safe(invoiceDetails.name));
      if (invoiceDetails.email) p.leftRight("Email", safe(invoiceDetails.email));
      if (invoiceDetails.phone) p.leftRight("Tel.", safe(invoiceDetails.phone));
      if (invoiceDetails.invoiceNumber) p.leftRight("Számla", safe(invoiceDetails.invoiceNumber));
    }
  }

  // Footer
  p.rule();
  p.align("center");
  if (order.bottomDescription) p.line(safe(order.bottomDescription)).newline();
  if (order.restaurantContact) {
    p.line("Kérdés és panasz esetén értesítendő:");
    p.line(safe(order.restaurantContact)).newline();
  }
  p.line("KÉSZPÉNZ ÁTVÉTELÉRE NEM JOGOSÍT!");
  p.line("NOT VALID FOR CASH PAYMENT!");
  if (order.footerText) p.newline().line(safe(order.footerText));
  p.newline().line("Köszönjük, hogy a vendégünk volt!").newline();
  p.size(1).line("Üzemelteti a Happy Gastro™");
  p.align("left");
  p.cut();

  return p.encode();
}

export default { renderReceipt, resolveCharWidth, formatPriceHu, EscPos };
